import type { MessageRow } from '../lib/api';

/**
 * Per docs/DESIGN-DECISIONS.md §1.4 (Dispatch strip), brand/BRAND.md §Surfaces
 * (stabilized V1-cloud patterns).
 *
 * The strip is the inline telemetry surface for a dispatch lifecycle. One
 * strip per dispatch_id, regardless of how many dispatch_output events came
 * through. The strip's body is a tracking-mono label and an optional dispatch
 * id suffix in JetBrains Mono. No content from the dispatch is shown; the
 * conversation stays readable. Output bytes accumulate into a running count.
 *
 * Resolution at finished is the label only. No completion glyph in V1.
 */

export interface DispatchGroup {
  dispatch_id: string;
  state: 'started' | 'running' | 'finished';
  total_bytes: number;
  final_meta?: Record<string, unknown>;
  first_seen: string;
}

export function DispatchStrip({ group }: { group: DispatchGroup }) {
  const label = renderLabel(group);
  const idSuffix = group.dispatch_id.slice(0, 8);
  return (
    <div className="flex">
      <div
        className="flex items-center gap-3 h-8 w-full px-3
                   border-l-2 rounded-sm"
        style={{
          background: 'var(--agent-bg-soft)',
          borderLeftColor: 'var(--agent-accent)',
        }}
      >
        <LivePulse stateActive={group.state !== 'finished'} />
        <span
          className="font-mono text-[10px] uppercase tracking-mono-wide flex-1"
          style={{ color: 'var(--agent-subtle)' }}
        >
          {label}
        </span>
        <span
          className="font-mono text-[10px]"
          style={{ color: 'var(--agent-fg-soft)' }}
        >
          {idSuffix}
        </span>
      </div>
    </div>
  );
}

function renderLabel(group: DispatchGroup): string {
  if (group.state === 'started' && group.total_bytes === 0) return 'dispatch · started';
  if (group.state === 'finished') {
    const error = group.final_meta?.error as { code?: string } | undefined;
    if (error?.code) return `dispatch · ${error.code.toLowerCase()}`;
    return `dispatch · finished · ${formatBytes(group.total_bytes)}`;
  }
  return `dispatch · ${formatBytes(group.total_bytes)}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Live-pulse glyph for the dispatch strip. simpliloquy-radio-pulse is the
 * canonical motion per BRAND.md §Motion. The outer ring expands and fades;
 * the inner dot stays steady. When the dispatch finishes, both freeze.
 */
function LivePulse({ stateActive }: { stateActive: boolean }) {
  return (
    <svg width={12} height={12} viewBox="0 0 16 16" aria-hidden="true">
      {stateActive && (
        <circle
          cx="8"
          cy="8"
          r="4"
          fill="none"
          stroke="var(--agent-accent)"
          strokeWidth="1.5"
          className="simpliloquy-radio-pulse"
          style={{ transformOrigin: '8px 8px', transformBox: 'fill-box' }}
        />
      )}
      <circle cx="8" cy="8" r="2.5" fill="var(--agent-accent)" />
    </svg>
  );
}

/**
 * Backwards-compat re-export so message-row consumers continue to compile.
 * The old per-row indicator is deprecated; ConversationPane now groups
 * dispatch_* messages into DispatchStrip via grouping logic.
 */
export function DispatchIndicator({ message: _message }: { message: MessageRow }) {
  return null;
}
